import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Inbox,
  Moon,
  Sun,
  LogOut,
  Settings2,
  HelpCircle,
} from "lucide-react";

import NavIconBtn from "./NavIconBtn";
import AnimatedA from "../AnimatedA";
import NavBreadcrumb from "./NavBreadcrumb";
import NavMoreMenu from "./NavMoreMenu";
import PopUpNotes from "../../Panels/PopUpNotes";
import PopUpTicket from "../../Panels/PopUpTicket";
import PopUpLogOut from "../../Panels/PopUpLogOut";
import PopUpSettings from "../../Settings/PopUpSettings";
import { useDark } from "../../Universal/useNavTheme";
import { usePopup } from "../../contexts/PopupContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLayout } from "../../contexts/LayoutContext";
import SearchTrigger from "../Search/SearchTrigger";
import { SHOW_LEGACY_CHAT } from "../constants";

const COLLAPSE_WIDTH = 680;

// AnimatedA moved to Navigation/AnimatedA.jsx (NAVBAR_REMOVAL_PLAN.md §3.1)
// so the GS floating sidebar toggle shares it. This navbar is DD-only now —
// GS unmounts it in Main.jsx.

// ─────────────────────────────────────────────────────────────────

export default function MainNavbar() {
  const dark = useDark();
  const navigate = useNavigate();
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();
  const { darkmode, setDarkmode } = useTheme();
  const { isLeftOpen, toggleLeft, isRightOpen, toggleRight, currentAppPage } =
    useLayout();

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const check = () => setCollapsed(window.innerWidth < COLLAPSE_WIDTH);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const TUTORIAL_PAGES = ["/graph"];
  const hasTutorial = TUTORIAL_PAGES.includes(currentAppPage);

  const startTutorial = () => {
    window.dispatchEvent(
      new CustomEvent("navbar:start-tutorial", {
        detail: { page: currentAppPage },
      }),
    );
  };

  const openLogout = () => {
    setPopUpComponent(<PopUpLogOut />);
    setIsPopUpOpen(true);
  };

  const openSettings = () => {
    setPopUpComponent(<PopUpSettings />);
    setIsPopUpOpen(true);
  };

  return (
    <div className="flex flex-row z-40 h-full w-full items-center justify-between min-h-[61px] px-4 md:px-5 py-1.5 md:py-2 border-b border-mainDivider bg-mainBg shadow-md backdrop-blur-md">
      {/* ── Left: sidebar toggle + breadcrumb ── */}
      <div className="flex flex-row items-center relative gap-x-2">
        <NavIconBtn onClick={toggleLeft} aria-label="Toggle left sidebar">
          {isLeftOpen ? (
            <ChevronLeft
              strokeWidth={1.5}
              className="h-5 w-5 text-mutedText/70 transition-colors duration-200"
            />
          ) : (
            <AnimatedA size={24} />
          )}
        </NavIconBtn>
        <SearchTrigger context="navbar" />
        {/* {!isLeftOpen && <NavBreadcrumb />}*/}
      </div>

      {/* ── Right: actions ── */}
      <div className="flex flex-row items-center relative gap-x-1">
        {!collapsed ? (
          <>
            {/* Inbox with unread badge — legacy chat page only; the new
                /threads page has no inbox, so this hides rather than repoints. */}
            {SHOW_LEGACY_CHAT && (
              <NavIconBtn
                onClick={() => navigate("/chat")}
                aria-label="Inbox"
                title="Inbox"
              >
                <span className="relative inline-flex">
                  <Inbox
                    strokeWidth={1.5}
                    className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                  />
                  {/* <span className="absolute -bottom-1.5 -right-2 z-10 flex h-[14px] min-w-[14px] items-center justify-center rounded-full border border-velBlue/40 bg-mainBg px-[3px] text-[9px] font-normal leading-none text-velBlue/80">
                    2
                  </span>*/}
                </span>
              </NavIconBtn>
            )}

            <PopUpTicket buttonNavbar={true} />
            <PopUpNotes buttonNavbar={true} />
            {/* {hasTutorial && (
              <NavIconBtn
                onClick={startTutorial}
                aria-label="Help & Tutorial"
                title="Help & Tutorial"
              >
                <HelpCircle
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
              </NavIconBtn>
            )}*/}
            <NavIconBtn
              onClick={() => setDarkmode(!darkmode)}
              aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
              title={dark ? "Light Mode" : "Dark Mode"}
            >
              {dark ? (
                <Sun
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
              ) : (
                <Moon
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
              )}
            </NavIconBtn>
            <NavIconBtn
              onClick={openSettings}
              aria-label="Settings"
              title="Settings"
            >
              <Settings2
                strokeWidth={1.5}
                className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
              />
            </NavIconBtn>
            <NavIconBtn
              onClick={openLogout}
              aria-label="Log out"
              title="Log out"
              danger
            >
              <LogOut
                strokeWidth={1.5}
                className="h-[18px] w-[18px] text-logoutColor transition-colors duration-200"
              />
            </NavIconBtn>
          </>
        ) : (
          <NavMoreMenu
            dark={dark}
            hasTutorial={hasTutorial}
            startTutorial={startTutorial}
            openSettings={openSettings}
            openLogout={openLogout}
          />
        )}

        {/* {isRightOpen && (
          <NavIconBtn
            onClick={toggleRight}
            aria-label="Close right sidebar"
            active={isRightOpen}
          >
            <ChevronRight
              strokeWidth={1}
              className={[
                "h-5 w-5 transition-colors duration-200",
                isRightOpen ? "text-velBlue" : "text-mutedText/60",
              ].join(" ")}
            />
          </NavIconBtn>
        )}*/}
      </div>
    </div>
  );
}
